import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { Message, TMessagesPaginated } from "@core/models/message.model";
import { MessageService } from "@core/services/message.service";
import { NotificationService } from "@core/services/notification.service";
import { Utilities } from "@core/utils/utils";
import { TPagination } from "@custom-types/sorting";
import { ToasterSeverities } from "@enums/toaster-severities";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import * as messageActions from "./message.actions";
import { SELECT_MESSAGE_PAGINATION } from "./message.selectors";

@Injectable()
export class MessageEffects {
  loadMessages$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(messageActions.LOAD_MESSAGES),
      withLatestFrom(this.store.select(SELECT_MESSAGE_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            messageActions.SET_MESSAGES_LOADING_STATUS({ loading: true })
          );
          return this.messageService
            .getMessages$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Message> | TMessagesPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    messageActions.SET_MESSAGES_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      messageActions.SET_MESSAGES({
                        messageList: data,
                      }),
                      messageActions.SET_MESSAGES_LENGTH({
                        length: response.length,
                      }),
                      messageActions.SET_MESSAGES_PAGINATION({ pagination })
                    );
                  }
                  return of(
                    messageActions.SET_MESSAGES({ messageList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  messageActions.SET_MESSAGES({ messageList: [] }),
                  messageActions.SET_MESSAGES_LENGTH({
                    length: 0,
                  }),
                  messageActions.SET_MESSAGES_PAGINATION({
                    pagination: undefined,
                  }),
                  messageActions.SET_MESSAGES_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  downloadMessageAttachment$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(messageActions.DOWNLOAD_MESSAGE_ATTACHMENT),
      mergeMap((action: { message: Message }): Observable<Action> => {
        return this.messageService
          .getMessageAttachment$(action.message.id)
          .pipe(
            switchMap((attachment: Blob): Observable<Action> => {
              Utilities.downloadAttachment(
                attachment,
                action.message.attachmentName
              );
              this.notificationService.show(
                ToasterSeverities.Success,
                "MESSAGE_ATTACHMENT_DOWNLOADED",
                undefined,
                { name: action.message.attachmentName }
              );
              return of();
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "MESSAGE_ATTACHMENT_DOWNLOAD_ERROR",
                response,
                { name: action.message.attachmentName }
              );
              return of();
            })
          );
      })
    );
  });

  saveMessage$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(messageActions.SAVE_MESSAGE),
      mergeMap(
        (action: {
          message: Partial<Message>;
          image?: File;
          userId?: string;
          intermediaryId?: string;
          productId?: string;
          productVersionId?: string;
        }): Observable<Action> => {
          this.store.dispatch(
            messageActions.SET_MESSAGES_LOADING_STATUS({ loading: true })
          );
          return this.messageService
            .saveMessage$(
              action.message,
              action.image,
              action.userId,
              action.intermediaryId,
              action.productId,
              action.productVersionId
            )
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  messageActions.SET_MESSAGES_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "MESSAGE_SENT",
                  undefined,
                  { name: action.message.subject ?? "" }
                );
                return of(messageActions.LOAD_MESSAGES({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "MESSAGE_SEND_ERROR",
                  response,
                  { name: action.message.subject ?? "" }
                );
                return of(
                  messageActions.SET_MESSAGES_LOADING_STATUS({ loading: false })
                );
              })
            );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private messageService: MessageService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
